import { useAppSignUpPageResultsUpdateEffect } from "@faire/deprecated-retailer-visitor-shared/components/AppSignUp/AppSignUpPageResultsProvider";
import { useFilterStore } from "@faire/deprecated-retailer-visitor-shared/components/Filters/useFilterStore";
import { NoResultsContainer } from "@faire/deprecated-retailer-visitor-shared/components/NoResults";
import { getSpacing } from "@faire/web/slate/spacing";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { ResponsiveValues } from "@faire/web/slate/Theme/ResponsiveValues";
import React from "react";
import styled from "styled-components";
import { css } from "styled-components";

import { useLoggedOutSuppliersPageCurrentCategory } from "../../../../Context/useLoggedOutSuppliersPageCurrentCategory";
import { useLoggedOutSuppliersPageBrandSearchResults } from "../../../../DataFetching/BrandsSearch/useLoggedOutSuppliersPageBrandSearchResults";
import { useLoggedOutSuppliersPageIsPending } from "../../../../DataFetching/BrandsSearch/useLoggedOutSuppliersPageIsLoading";
import { useLoggedOutSuppliersPageQueryParams } from "../../../../QueryParams/useLoggedOutSuppliersPageQueryParams";

import { BrandTile } from "./__internal__/BrandTile";
import {
  BrandTilesLoadingSkeletonGrid,
  useGetItemsPerRowResponsive,
} from "./__internal__/helpers";

export const LoggedOutSuppliersPageBrandResults: React.FC<{
  loadTimingRef?: React.Ref<HTMLDivElement>;
}> = ({ loadTimingRef }) => {
  const brandSearchResults = useLoggedOutSuppliersPageBrandSearchResults();
  const isLoading = useLoggedOutSuppliersPageIsPending();
  const categoryModel = useLoggedOutSuppliersPageCurrentCategory();
  const { clearFilters } = useLoggedOutSuppliersPageQueryParams();
  const {
    data: { activeFilterCount, isSideBarVisible },
  } = useFilterStore(["activeFilterCount", "isSideBarVisible"]);
  const itemsPerRowResponsive = useGetItemsPerRowResponsive(isSideBarVisible);

  useAppSignUpPageResultsUpdateEffect({
    suppliersPageBrands: brandSearchResults,
  });

  if (isLoading) {
    return <BrandTilesLoadingSkeletonGrid />;
  }

  if (!brandSearchResults || brandSearchResults.length === 0) {
    return (
      <NoResultsContainer
        showProductFirstResults={false}
        hasFiltersApplied={activeFilterCount > 0}
        onClearFilters={clearFilters}
        divRef={loadTimingRef}
      />
    );
  }

  return (
    <BrandResultsListWrapper $columns={itemsPerRowResponsive}>
      {brandSearchResults.map((brand) => (
        <React.Fragment key={`${brand?.token}`}>
          <BrandTile
            brand={brand}
            categoryPathIdentifier={categoryModel.pathIdentifier ?? ""}
          />
        </React.Fragment>
      ))}
    </BrandResultsListWrapper>
  );
};

const BrandResultsListWrapper = styled.div<{
  $columns: ResponsiveValues<number>;
}>`
  position: relative;
  display: grid;
  column-gap: ${getSpacing("1x")};

  ${responsiveMediaQueries.mobile} {
    column-gap: 0;
    grid-template-columns: ${({ $columns }) =>
      css`[start] repeat(${$columns.mobileAndAbove}, minmax(0, 1fr)) [end]`};
  }
  ${responsiveMediaQueries.tablet} {
    column-gap: 0;
    grid-template-columns: ${({ $columns }) =>
      css`[start] repeat(${$columns.tabletAndAbove}, minmax(0, 1fr)) [end]`};
  }
  ${responsiveMediaQueries.desktop} {
    grid-template-columns: ${({ $columns }) =>
      css`[start] repeat(${$columns.desktopAndAbove}, minmax(0, 1fr)) [end]`};
  }
  ${responsiveMediaQueries.xlarge} {
    grid-template-columns: ${({ $columns }) =>
      css`[start] repeat(${$columns.xLargeAndAbove}, minmax(0, 1fr)) [end]`};
  }
  ${responsiveMediaQueries.xxlarge} {
    grid-template-columns: ${({ $columns }) =>
      css`[start] repeat(${$columns.xxLargeDesktop}, minmax(0, 1fr)) [end]`};
  }
`;
